import React from 'react';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Link } from 'react-router-dom';

import PaginationControl from '../../../common/components/PaginationControl';
import { Pages } from '../../../common/constants/Pages';
import { theme } from '../../../common/theme/theme';
import { SheetStatus } from '../../../common/types/SaveSheet';
import { SearchResult } from '../../../common/types/SearchResult';
import { displayDateString } from '../../../common/utils/DateUtils';

import ChangeNoteStatusPopup from './components/ChangeNoteStatusPopup';

import '../TableStyles.scss';
import { useAppSelector } from '../../../common/hooks/reduxHooks';
import { CurrentUser, UserRoles } from '../../../common/types/User';

import { useUserRole } from 'src/common/hooks/useUserRole';

export type Order = 'asc' | 'desc';

type HeaderCell = {
  id: keyof SearchResult;
  label: string;
  numeric: boolean;
};

const headerCells: readonly HeaderCell[] = [
  {
    id: 'id',
    label: 'ID',
    numeric: true,
  },
  {
    id: 'type',
    label: 'Type',
    numeric: false,
  },
  {
    id: 'documentName',
    label: 'File Name',
    numeric: false,
  },
  {
    id: 'text',
    label: 'Summary',
    numeric: false,
  },
  {
    id: 'status',
    label: 'Status',
    numeric: false,
  },
  {
    id: 'hasFlaggedCodes',
    label: 'Flagged',
    numeric: false,
  },
  {
    id: 'updatedAt',
    label: 'Last updated',
    numeric: false,
  },
  {
    id: 'organizationName',
    label: 'Organization',
    numeric: false,
  },
  {
    id: 'userEmail',
    label: 'User',
    numeric: false,
  },
];

interface AdminTableHeadProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof SearchResult
  ) => void;
  order: Order;
  orderBy: string;
  user: CurrentUser | null;
}

const AdminTableHead: React.FC<AdminTableHeadProps> = ({
  onRequestSort,
  order = 'desc',
  orderBy = 'id',
  user,
}) => {
  const createSortHandler =
    (property: keyof SearchResult) => (event: React.MouseEvent<unknown>) => {
      if (property === 'hasFlaggedCodes') return;
      onRequestSort(event, property);
    };

  const conditionedHeaderCells = user?.accessRoles.includes(
    UserRoles.SUPER_ADMIN
  )
    ? headerCells
    : headerCells.filter((row) => row.id !== 'organizationName');

  return (
    <TableHead
      sx={{
        backgroundColor: (theme) => theme.palette.primaryLight?.main,
        height: 52,
      }}
    >
      <TableRow>
        {conditionedHeaderCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={
                headCell.id === 'hasFlaggedCodes'
                  ? false
                  : orderBy === headCell.id
              }
              hideSortIcon={headCell.id === 'hasFlaggedCodes'}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              IconComponent={() => {
                if (orderBy !== headCell.id) {
                  return <KeyboardArrowUpRoundedIcon />;
                }
                return order === 'desc' ? (
                  <KeyboardArrowDownRoundedIcon />
                ) : (
                  <KeyboardArrowUpRoundedIcon />
                );
              }}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

type AdminTableProps = {
  results: SearchResult[];
  isLoading: boolean;
  total: number;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  requestSort: (property: keyof SearchResult, order: Order) => void;
};

const ResultsTable: React.FC<AdminTableProps> = ({
  results,
  isLoading,
  total,
  page,
  setPage,
  rowsPerPage,
  requestSort,
}) => {
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof SearchResult>('id');
  const [selectedNote, setSelectedNote] = React.useState<
    SearchResult | undefined
  >(undefined);
  const { user } = useAppSelector((state) => state.model);
  const { userRole } = useUserRole();

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof SearchResult
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    requestSort(property, isAsc ? 'desc' : 'asc');
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const emptyRows =
    total > 0
      ? page > 0
        ? Math.max(0, page * rowsPerPage - total)
        : 0
      : rowsPerPage;

  const getBackgroundClass = (status: SheetStatus | 'search' | undefined) => {
    if (status === SheetStatus.UPLOADED) return 'rgba(82, 86, 98, 0.2)';
    if (status === SheetStatus.PROCESSED) return 'rgba(38, 187, 255, 0.3)';
    if (status === SheetStatus.DRAFT) return 'rgba(241, 157, 96, 0.3)';
    if (status === SheetStatus.FINALIZED) return 'rgba(109, 223, 198, 0.3)';

    return 'rgba(34, 123, 239, 0.3)';
  };

  const renderStatusBubble = (result: SearchResult) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        className='status-bubble-container'
        style={{
          backgroundColor: getBackgroundClass(result.status as SheetStatus),
        }}
        onClick={() =>
          result.status === 'Finalized' ? setSelectedNote(result) : null
        }
      >
        <Typography fontSize={16} color='black' fontWeight={500}>
          {result.status ?? 'Search'}
        </Typography>
      </div>
      {result.status === 'Finalized' && (
        <KeyboardArrowDownRoundedIcon onClick={() => setSelectedNote(result)} />
      )}
    </div>
  );

  return (
    <Box sx={{ minWidth: 400 }}>
      <TableContainer>
        <Table>
          <AdminTableHead
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            user={user}
          />
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 0 }} colSpan={9}>
                  <CircularProgress
                    color='primary'
                    size='64px'
                    style={{ display: 'block', margin: '24px auto' }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!isLoading && (
            <TableBody>
              {results.map((result) => (
                <TableRow
                  itemID={`tr-${result.id.toString()}`}
                  key={result.id.toString()}
                  className={'admin-table-row'}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell sx={{ minWidth: 20, maxWidth: 120 }}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={'primary'}
                        fontSize={16}
                      >
                        {result.id}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell sx={{ minWidth: 20, maxWidth: 100 }}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                        fontSize={16}
                      >
                        {result.type}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell sx={{ minWidth: 40, maxWidth: 220 }}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={'black'}
                        fontSize={16}
                      >
                        {result.isSmartLaunch
                          ? 'Smart Launch'
                          : result.documentName?.length
                          ? result.documentName
                          : '-'}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180, maxWidth: 520 }}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                        fontSize={16}
                      >
                        {result.text.substring(0, 120)}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>{renderStatusBubble(result)}</TableCell>
                  <TableCell sx={{ minWidth: 60, maxWidth: 90 }}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={
                          result.hasFlaggedCodes
                            ? theme.palette.error.dark
                            : theme.palette.text.secondary
                        }
                        fontSize={16}
                      >
                        {result.hasFlaggedCodes ? 'Yes' : 'No'}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.result, result.id)}
                    >
                      <Typography
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                        fontSize={16}
                      >
                        {displayDateString(result.updatedAt, false)}
                      </Typography>
                    </Link>
                  </TableCell>
                  {user?.accessRoles.includes(UserRoles.SUPER_ADMIN) && (
                    <TableCell sx={{ minWidth: 60, maxWidth: 160 }}>
                      {result.organizationId ? (
                        <Link
                          className='unstyled-link underline-on-hover full-width-block'
                          to={Pages.getRoute(
                            Pages.editOrganization,
                            result.organizationId
                          )}
                        >
                          <Typography
                            whiteSpace='normal'
                            fontWeight={500}
                            color={'black'}
                            fontSize={16}
                            style={{
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            }}
                          >
                            {result.organizationName}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography
                          whiteSpace='normal'
                          fontWeight={500}
                          color={'black'}
                          fontSize={16}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: 60, maxWidth: 160 }}>
                    {userRole === UserRoles.CODER ? (
                      <Typography
                        whiteSpace='normal'
                        fontWeight={500}
                        color={'black'}
                        fontSize={16}
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {result.userEmail}
                      </Typography>
                    ) : (
                      <Link
                        className='unstyled-link underline-on-hover full-width-block'
                        to={Pages.getRoute(Pages.user, result.userId)}
                      >
                        <Typography
                          whiteSpace='normal'
                          fontWeight={500}
                          color={'black'}
                          fontSize={16}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {result.userEmail}
                        </Typography>
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell sx={{ border: 0 }} colSpan={5} />
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && results.length > 0 && (
        <PaginationControl
          count={Math.ceil(total / rowsPerPage)}
          onChange={(e, p) => setPage(p)}
          page={page}
        />
      )}
      <Backdrop
        open={!!selectedNote}
        sx={{
          color: '#fff',
          zIndex: 99,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {selectedNote && (
          <ChangeNoteStatusPopup
            searchResult={selectedNote}
            onClickClose={() => setSelectedNote(undefined)}
          />
        )}
      </Backdrop>
    </Box>
  );
};

export default ResultsTable;
